import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import CollapsibleContainer from "@via/components/collapsible-container/collapsible-container.tsx";
import { GenericError } from "@via/components/error.tsx";
import FileInputButton from "@via/components/inputs/file-input-button.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import FormTextareaComponent from "@via/components/inputs/form-textarea-component";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { HeadingSize, PageHeading } from "@via/components/page-heading.tsx";
import ToggleButton from "@via/components/toggle-button.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import InputSingleFileModal from "@via/pages/projects/runs/overview/inputs/input-file/input-single-file-modal.tsx";
import {
  convertAndCapitalizeString,
  filterDuplicatesByProperty,
  getPipeInputs,
  type InputNode,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { isObject, isObjectEmpty } from "@via/utils/helpers.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import { createMarkup } from "@via/utils/html-utils.tsx";
import {
  type GetRunDetailsResponseBody,
  type RunParameterListResponseBody,
} from "common";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  type FC,
  type JSX,
} from "react";
import { type UseFormReturn } from "react-hook-form";
import { Tooltip } from "react-tooltip";

const sort: Array<{ id: string; desc: boolean }> = [
  {
    id: "name",
    desc: false,
  },
];

interface ExternalPipelineInputsProps {
  form: UseFormReturn;
  setRequiredGlobalInputs: Dispatch<SetStateAction<InputNode[]>>;
  pipelineRunDetails: RunParameterListResponseBody;
  runSettingsData: GetRunDetailsResponseBody;
  isRunSettingsSuccess: boolean;
  isRunSettingsLoading: boolean;
  isRunSettingsError: boolean;
  readOnly: boolean;
}

const ExternalPipelineInputs: FC<ExternalPipelineInputsProps> = ({
  form,
  setRequiredGlobalInputs,
  pipelineRunDetails,
  runSettingsData,
  isRunSettingsSuccess,
  isRunSettingsLoading,
  isRunSettingsError,
  readOnly,
}) => {
  const { control, setValue, getValues } = form;

  const [openInputSingleFileModal, setOpenInputSingleFileModal] =
    useState<Record<string, unknown> | null>(null);
  const [singleFileEditor, setSingleFileEditor] = useState<
    Record<string, any> | undefined
  >();
  const [showHiddenInputs, setShowHiddenInputs] = useState(true);
  const [tableKey, setTableKey] = useState(0);

  const isRunInputsLoading = isRunSettingsLoading;
  const isRunInputsError = isRunSettingsError;

  useEffect(() => {
    if (isRunSettingsSuccess) {
      const { inputs } = runSettingsData;
      Object.values(pipelineRunDetails?.inputs)?.forEach(({ schema }) => {
        const { pipeParamsArray } = getPipeInputs(schema);
        const globalInputs = filterDuplicatesByProperty(
          pipeParamsArray,
          "name"
        );
        // Remove duplicates before updating state
        setRequiredGlobalInputs(prevRequiredGlobalInputs => {
          const uniqueRequiredInputs = filterDuplicatesByProperty(
            [
              ...prevRequiredGlobalInputs,
              ...globalInputs.filter(
                ({ inputDetails }) => !inputDetails?.optional
              ),
            ],
            "name"
          );
          return uniqueRequiredInputs;
        });
        // Set input default values
        globalInputs
          .filter(({ inputDetails }) => inputDetails?.defVal)
          .forEach(({ id, inputDetails }) => {
            setValue(
              id,
              inputDetails.checkbox
                ? inputDetails.defVal === "true" || inputDetails.defVal === true
                : inputDetails.defVal
            );
          });
      });

      // Reset input values if inputs are present before
      if (!isObjectEmpty(inputs)) {
        const currentParams = getValues("params") || {};
        const validKeys = Object.entries(inputs || {}).flatMap(([key, value]) =>
          Object.keys(value)
            .map(subKey =>
              !["email", "outdir", "tracedir"].includes(subKey)
                ? `${key}.${subKey}`
                : null
            )
            .filter(Boolean)
        );
        Object.keys(currentParams).forEach(paramKey => {
          const value = currentParams[paramKey];
          if (isObject(value)) {
            Object.keys(value).forEach(subKey => {
              const flatKey = `${paramKey}.${subKey}`;
              if (
                !validKeys.includes(flatKey) &&
                !["email", "outdir", "tracedir"].includes(subKey)
              ) {
                setValue(`params.${flatKey}`, undefined);
              }
            });
          }
        });
      }

      // Set input values from API response
      Object.entries(inputs || {}).forEach(([key, value]) => {
        Object.entries(value)
          // Don't set outdir, email and tracedir values if they already exist in the pipelineRunDetails.
          // Foundry already have this functionality, so we don’t want to save these values into inputs_json.
          .filter(
            ([subKey, _]) => !["email", "outdir", "tracedir"].includes(subKey)
          )
          .forEach(([subKey, subValue]) => {
            setValue(`params.${key}.${subKey}`, subValue);
          });
      });
    }
  }, [pipelineRunDetails, runSettingsData, isRunSettingsSuccess, setValue]);

  useEffect(() => {
    if (isRunSettingsSuccess) {
      setTableKey(prev => prev + 1); // Force rerender by changing key
    }
  }, [isRunSettingsSuccess, runSettingsData]);

  const renderSelectComponent = (info): JSX.Element => (
    <div className="flex w-full">
      <div className="mr-3 w-80">
        <SelectComponent
          control={control}
          name={info.row.original.id}
          options={
            info
              .getValue()
              ?.dropDown?.split(",")
              .map(option => {
                return {
                  label: option,
                  value:
                    info.getValue()?.valueType === "number" ||
                    info.getValue()?.valueType === "integer"
                      ? Number(option)
                      : option,
                };
              }) ?? []
          }
          readOnly={readOnly}
        />
      </div>
    </div>
  );

  const renderTextInputComponent = (info): JSX.Element => (
    <FormInputComponent
      control={control}
      name={info.row.original.id}
      disabled={readOnly}
      className="w-80 -mt-2"
    />
  );

  const renderNumberInputComponent = (info): JSX.Element => (
    <FormInputComponent
      control={control}
      name={info.row.original.id}
      type="number"
      disabled={readOnly}
      className="w-80 -mt-2"
      // #TODO: Add validation for min and max later
      // min={0}
      // max={100}
    />
  );

  const renderTextareaComponent = (info): JSX.Element => (
    <FormTextareaComponent
      control={control}
      name={info.row.original.id}
      disabled={readOnly}
      className="w-80 -mt-2"
    />
  );

  const renderCheckboxComponent = (info): JSX.Element => (
    <FormInputComponent
      control={control}
      name={info.row.original.id}
      type="checkbox"
      disabled={readOnly}
      className="h-6 w-6 -mt-2"
    />
  );

  const renderFileButton = (info): JSX.Element => (
    <FileInputButton
      control={control}
      name={info.row.original.id}
      onOpenModal={value => {
        if (info.row.original.qualifier === "singleFile") {
          setOpenInputSingleFileModal({
            [info.row.original.id]: value,
          });
          setSingleFileEditor(info.row.original?.inputDetails?.editor);
        }
      }}
      onDeleteFile={() => {
        if (info.row.original.qualifier === "singleFile") {
          handleDeleteSingleInputFile(info.row.original.id);
        }
      }}
      disabled={readOnly}
    />
  );

  const renderInputDescription = (info): JSX.Element => (
    <div
      className="mt-1 max-w-[90vw] whitespace-normal text-xs italic text-gray-500"
      dangerouslySetInnerHTML={createMarkup(info)}
    />
  );

  const columnHelper = createColumnHelper<InputNode>();
  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        cell: info => (
          <div className="flex flex-row items-center align-top">
            {info.row.original.inputDetails?.inLabelOpt ?? info.getValue()}{" "}
            {info.row.original.inputDetails?.optional && "(Optional)"}
            {info.row.original.inputDetails?.tool && (
              <InformationCircleIcon
                data-tooltip-id={`info-${info.row.original.id}`}
                data-tooltip-content={info.row.original.inputDetails.tool}
                className="mx-2 h-4"
              />
            )}
            <Tooltip
              id={`info-${info.row.original.id}`}
              className="z-50 max-w-xs whitespace-pre-line break-words"
            />
          </div>
        ),
        header: "Name",
      }),
      columnHelper.accessor("inputDetails", {
        cell: info => (
          <div>
            {info.getValue()?.dropDown
              ? renderSelectComponent(info)
              : info.getValue()?.checkbox
                ? renderCheckboxComponent(info)
                : info.getValue()?.textbox
                  ? renderTextareaComponent(info)
                  : info.row.original.qualifier === "val"
                    ? renderTextInputComponent(info)
                    : info.row.original.qualifier === "number"
                      ? renderNumberInputComponent(info)
                      : renderFileButton(info)}
            {info.getValue()?.inDescOpt &&
              renderInputDescription(info.getValue()?.inDescOpt)}
          </div>
        ),
        header: "Input",
        size: 375,
        minSize: 150,
      }),
    ] as Array<ColumnDef<InputNode>>;
  }, [readOnly]);

  const handleSaveSingleInputFile = (
    inputId: string,
    value: string | null
  ): void => {
    setValue(inputId, value);
  };

  const handleDeleteSingleInputFile = (inputId: string): void => {
    setValue(inputId, null);
  };

  return (
    <>
      <div className="mb-4 px-4 py-2 shadow-md ring-1 ring-gray-900/5 sm:mx-0 rounded-lg md:px-6 lg:px-6 md:py-3 lg:py-3 lg:col-span-2 lg:row-span-2 lg:row-end-2">
        <div className="flex items-center justify-between">
          <PageHeading title="Inputs" size={HeadingSize.Small} />
          <div className="flex items-center">
            <ToggleButton
              isOn={showHiddenInputs}
              onToggle={() => setShowHiddenInputs(!showHiddenInputs)}
            />
            <span className="ml-1 text-sm font-medium leading-6 text-gray-700">
              Show Hidden Inputs
            </span>
          </div>
        </div>
        {isRunInputsLoading ? (
          <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
            <SkeletonLoader count={12} />
          </div>
        ) : isRunInputsError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : (
          <>
            {Object.values(pipelineRunDetails?.inputs)?.map(
              ({ schema, details }, index) => {
                const { pipeParamsArray } = getPipeInputs(schema);
                const globalInputs = filterDuplicatesByProperty(
                  pipeParamsArray,
                  "name"
                );
                const inputs = globalInputs.filter(
                  ({ inputDetails }) =>
                    showHiddenInputs || !inputDetails?.hidden
                );

                if (inputs.length === 0) return null;

                return (
                  <CollapsibleContainer
                    key={`externalPipelineInputOptions-${runSettingsData?.runUUID}-${index}`}
                    defaultOpen
                    size={HeadingSize.XSmall}
                    title={convertAndCapitalizeString(details.longVarName)}>
                    {renderInputDescription(details.description)}
                    <Table
                      key={tableKey}
                      data={inputs}
                      sort={sort}
                      columns={columns}
                      showPagination={false}
                      pageSize={inputs.length}
                      size={TableSize.Small}
                      applyColumnSize={true}
                    />
                  </CollapsibleContainer>
                );
              }
            )}
          </>
        )}
        {!!openInputSingleFileModal && (
          <InputSingleFileModal
            value={openInputSingleFileModal}
            setValue={setOpenInputSingleFileModal}
            onSave={handleSaveSingleInputFile}
            form={form}
            editor={singleFileEditor}
            runUUID={runSettingsData?.runUUID}
          />
        )}
      </div>
    </>
  );
};

export default ExternalPipelineInputs;
